/**
 * The recovery offer (§23).
 *
 * §23 promises that unsaved edits survive a crash. The controller has been
 * writing recovery points all along; without this screen the promise was kept
 * and never delivered — the bytes were on disk and nothing offered them back.
 *
 * Two rules shape how it behaves.
 *
 * **Recovery is offered, never applied.** The app does not decide that
 * yesterday's unsaved edit is what someone wants to see today. It says what it
 * has and lets them choose, because restoring silently would replace a
 * document the user just opened with one they had forgotten about.
 *
 * **Discarding is explicit and per item.** A "clear all" that removes several
 * people's afternoon in one click is a button worth not having.
 */
import { useCallback, useEffect, useState } from 'react';
import { findRecoverableWork, formatFileSize, formatRelativeTime } from '@moduviewer/core';
import type { FileStore, RecoverableWork } from '@moduviewer/core';
import { Icon, iconForFormat } from '@moduviewer/design/icon';
import { FORMATS_BY_ID } from '@moduviewer/core';
import {
  allRecoveryLabels, forgetRecoveryLabel, pruneRecoveryLabels, recoveryLabel,
} from '../lib/recovery-index.js';

export interface RecoverableItem {
  readonly work: RecoverableWork;
  /** What the file was called, when the index still knows. */
  readonly displayName: string | null;
  readonly formatId: string | null;
}

export interface RecoveryOfferProps {
  readonly store: FileStore;
  /** Restore one item: read its bytes and open them as a document. */
  readonly onRecover: (item: RecoverableItem, bytes: Uint8Array) => void | Promise<void>;
}

export function RecoveryOffer({ store, onRecover }: RecoveryOfferProps): React.JSX.Element | null {
  const [items, setItems] = useState<RecoverableItem[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const work = await findRecoverableWork(store, Date.now());
      // The index must not outlive the data: an offer to recover something the
      // store no longer holds would fail the moment it was accepted.
      pruneRecoveryLabels(work.map((w) => w.sessionId));

      setItems(work.map((w) => {
        const label = recoveryLabel(w.sessionId);
        return {
          work: w,
          displayName: label?.displayName ?? null,
          formatId: label?.formatId ?? null,
        };
      }));
    } catch {
      // A store that cannot list recovery points is not a reason to block the
      // app; the user came here to open a file (§34).
      setItems([]);
    }
  }, [store]);

  useEffect(() => { void load(); }, [load]);

  const recover = useCallback(async (item: RecoverableItem) => {
    setFailure(null);
    setBusy(item.work.sessionId);
    try {
      const bytes = await store.readRecoveryPoint(item.work.latest);
      await onRecover(item, bytes);
      forgetRecoveryLabel(item.work.sessionId);
      await load();
    } catch (err) {
      setFailure((err as Error).message || '복구하지 못했습니다');
    } finally {
      setBusy(null);
    }
  }, [store, onRecover, load]);

  const discard = useCallback(async (item: RecoverableItem) => {
    setBusy(item.work.sessionId);
    try {
      // Every point for the session, not just the latest — leaving the older
      // ones would make the offer reappear on the next launch.
      const all = await store.listRecoveryPoints(item.work.sessionId);
      await store.deleteRecoveryPoints(all.length > 0 ? all : [item.work.latest]);
      forgetRecoveryLabel(item.work.sessionId);
      await load();
    } catch (err) {
      setFailure((err as Error).message || '삭제하지 못했습니다');
    } finally {
      setBusy(null);
    }
  }, [store, load]);

  if (items === null || items.length === 0) return null;

  return (
    <section className="recovery" aria-labelledby="recovery-title">
      <h2 className="recovery__title" id="recovery-title">
        <Icon name="clock" size={15} />
        저장하지 않은 편집 내용이 있습니다
      </h2>
      <p className="recovery__lead">
        마지막으로 편집한 내용을 그대로 가지고 있습니다. 원본 파일은 변경되지 않았습니다.
      </p>

      <ul className="recovery__list">
        {items.map((item) => (
          <li key={item.work.sessionId} className="recovery-item">
            <Icon
              name={item.formatId
                ? iconForFormat(item.formatId, FORMATS_BY_ID.get(item.formatId)?.family)
                : 'file'}
              size={18}
              className="recovery-item__icon"
            />
            <span className="recovery-item__text">
              <span className="recovery-item__name truncate">
                {item.displayName ?? '이름을 알 수 없는 문서'}
              </span>
              <span className="recovery-item__meta">
                <span>{formatRelativeTime('ko', new Date(item.work.latest.createdAtMs), new Date())}</span>
                <span aria-hidden="true">·</span>
                <span className="tnum">
                  {item.work.pointCount}개 지점
                </span>
                {item.displayName === null ? (
                  <>
                    <span aria-hidden="true">·</span>
                    {/* Honest about the degradation rather than inventing a
                        name: the edit is intact, only its label was lost. */}
                    <span>편집 내용은 그대로입니다</span>
                  </>
                ) : null}
              </span>
            </span>

            <span className="spacer" />

            <button
              className="btn btn--primary"
              disabled={busy !== null}
              onClick={() => void recover(item)}
            >
              복구
            </button>
            <button
              className="btn"
              disabled={busy !== null}
              onClick={() => void discard(item)}
            >
              삭제
            </button>
          </li>
        ))}
      </ul>

      {failure ? (
        <p className="recovery__failure">
          <Icon name="alert" size={13} />
          {failure}
        </p>
      ) : null}
    </section>
  );
}

/** Labels with no matching recovery point, for the §31 information screen. */
export function orphanedRecoveryLabels(live: readonly string[]): number {
  const known = new Set(live);
  return allRecoveryLabels().filter((l) => !known.has(l.sessionId)).length;
}

/** Bytes of a recovery point, sized for display. */
export function describeRecovery(item: RecoverableItem, bytes: number): string {
  return `${item.displayName ?? '문서'} · ${formatFileSize('ko', bytes)}`;
}
